/**
 * HowToStartSection - 3-step onboarding guide
 * Shows the simple path: Solicita demo → Configura → Activa
 */
import React from 'react';
import { Typography, Box, Container, Button } from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import TuneIcon from '@mui/icons-material/Tune';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import { COLORS, TYPOGRAPHY, RADIUS, GRADIENTS, TRANSITIONS } from './landingTheme';
import { ScrollReveal, StaggerReveal } from './animations';

const STEPS = [
  {
    number: '01',
    icon: <WhatsAppIcon />,
    title: 'Solicita tu demo',
    description: 'Escríbenos por WhatsApp y agenda una demostración personalizada para tu consultorio.',
    color: '#25D366',
    gradient: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
  },
  {
    number: '02',
    icon: <TuneIcon />,
    title: 'Configura tu chatbot',
    description: 'Nuestro equipo te acompaña a personalizar tu asistente: personalidad, horarios y base de conocimiento.',
    color: '#f59e0b',
    gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
  },
  {
    number: '03',
    icon: <RocketLaunchIcon />,
    title: 'Activa en WhatsApp',
    description: 'Tu chatbot comienza a atender pacientes 24/7. Diagnóstico, citas y memoria desde el día uno.',
    color: '#8b5cf6',
    gradient: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
  },
];

const StepCard = ({ step, index, total }) => (
  <Box sx={{ position: 'relative', textAlign: 'center', flex: 1, minWidth: 0 }}>
    {/* Connector line (desktop only, between cards) */}
    {index < total - 1 && (
      <Box
        sx={{
          display: { xs: 'none', md: 'block' },
          position: 'absolute',
          top: 44,
          left: '60%',
          width: '80%',
          height: 2,
          background: `linear-gradient(90deg, ${step.color}40, ${STEPS[index + 1].color}40)`,
          zIndex: 0,
        }}
      />
    )}

    {/* Icon circle */}
    <Box
      sx={{
        width: 88,
        height: 88,
        borderRadius: '50%',
        background: step.gradient,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mx: 'auto',
        mb: 2.5,
        position: 'relative',
        zIndex: 1,
        boxShadow: `0 12px 32px ${step.color}30`,
        '& svg': { fontSize: 36, color: '#fff' },
      }}
    >
      {step.icon}
    </Box>

    {/* Step number */}
    <Typography
      sx={{
        fontSize: '0.7rem',
        fontWeight: 700,
        color: step.color,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        mb: 1,
      }}
    >
      Paso {step.number}
    </Typography>

    {/* Title */}
    <Typography
      sx={{
        fontSize: TYPOGRAPHY.h3,
        fontWeight: 700,
        color: COLORS.textPrimary,
        mb: 1,
      }}
    >
      {step.title}
    </Typography>

    {/* Description */}
    <Typography
      sx={{
        fontSize: TYPOGRAPHY.small,
        color: COLORS.textSecondary,
        lineHeight: 1.7,
        maxWidth: 280,
        mx: 'auto',
      }}
    >
      {step.description}
    </Typography>
  </Box>
);

export default function HowToStartSection() {
  return (
    <Box
      sx={{
        py: { xs: 8, md: 12 },
        background: COLORS.white,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Subtle background accent */}
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 600,
          height: 600,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(245, 158, 11, 0.04) 0%, transparent 60%)',
          pointerEvents: 'none',
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <ScrollReveal>
          <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
            <Typography
              sx={{
                fontSize: '0.75rem',
                fontWeight: 700,
                color: COLORS.accent,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                mb: 1.5,
              }}
            >
              Comenzar es simple
            </Typography>
            <Typography
              sx={{
                fontSize: TYPOGRAPHY.h2,
                fontWeight: 800,
                color: COLORS.textPrimary,
                letterSpacing: '-0.02em',
                mb: 1.5,
              }}
            >
              Tres pasos para{' '}
              <Box
                component="span"
                sx={{
                  background: GRADIENTS.textGradient,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                transformar tu consultorio
              </Box>
            </Typography>
            <Typography
              sx={{
                fontSize: TYPOGRAPHY.body,
                color: COLORS.textSecondary,
                maxWidth: 480,
                mx: 'auto',
                lineHeight: 1.7,
              }}
            >
              Sin complicaciones técnicas. Nuestro equipo te acompaña en cada paso del proceso.
            </Typography>
          </Box>
        </ScrollReveal>

        {/* Steps */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: { xs: 5, md: 4 },
            mb: { xs: 5, md: 6 },
          }}
        >
          {STEPS.map((step, idx) => (
            <ScrollReveal key={idx} delay={idx * 0.15}>
              <StepCard step={step} index={idx} total={STEPS.length} />
            </ScrollReveal>
          ))}
        </Box>

        {/* CTA */}
        <ScrollReveal delay={0.3}>
          <Box sx={{ textAlign: 'center' }}>
            <Button
              component="a"
              href="https://wa.me/573052608968?text=Hola%2C%20me%20interesa%20una%20demo%20de%20NARA%20para%20mi%20consultorio"
              target="_blank"
              rel="noopener"
              variant="contained"
              startIcon={<WhatsAppIcon sx={{ fontSize: '20px !important' }} />}
              endIcon={<ArrowForwardIcon sx={{ fontSize: '16px !important' }} />}
              sx={{
                background: GRADIENTS.primaryButton,
                color: '#0f2440',
                fontWeight: 700,
                px: 4,
                py: 1.4,
                fontSize: '1rem',
                borderRadius: RADIUS.md,
                boxShadow: '0 4px 20px rgba(245, 158, 11, 0.35)',
                transition: TRANSITIONS.normal,
                textTransform: 'none',
                '&:hover': {
                  background: GRADIENTS.primaryButtonHover,
                  boxShadow: '0 8px 30px rgba(245, 158, 11, 0.45)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              Solicitar demo gratuita
            </Button>
          </Box>
        </ScrollReveal>
      </Container>
    </Box>
  );
}
